import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { UserRole } from '../../data/types';
import { authService } from '../../services/authService';
// Import AuthContext from App
import { AuthContext } from '../../App';

interface MainLayoutProps {
  children: React.ReactNode;
  userName: string;
  userAvatar?: string;
  userRole: UserRole;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  userName,
  userAvatar,
  userRole,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract the active item from the current path
  const getActiveItemFromPath = (path: string): string => {
    // Default to dashboard
    if (path === '/dashboard') return 'dashboard';
    
    // Handle teacher learning paths
    if (path.startsWith('/learning-paths/')) {
      const subPath = path.split('/')[2];
      return `learning-paths-${subPath}`;
    }
    

    
    // Handle superadmin learning paths
    if (path.startsWith('/superadmin/learning-paths')) {
      if (path === '/superadmin/learning-paths') {
        return 'superadmin-learning-paths';
      } else if (path === '/superadmin/learning-paths/create') {
        return 'superadmin-learning-paths-create';
      } else if (path === '/superadmin/learning-paths/assign') {
        return 'superadmin-learning-paths-assign';
      } else if (path.includes('/edit/')) {
        return 'superadmin-learning-paths';
      }
    }
    
    // Handle admin reports
    if (path.startsWith('/admin/reports/')) {
      const reportType = path.split('/')[3];
      return `reports-${reportType}`;
    }
    
    // Handle classes
    if (path.startsWith('/classes/')) {
      return 'classes';
    }
    
    // Handle other routes by removing the leading "/"
    return path.substring(1);
  };

  const [activeItem, setActiveItem] = useState(getActiveItemFromPath(location.pathname));

  // Update active item when location changes
  useEffect(() => {
    setActiveItem(getActiveItemFromPath(location.pathname));
  }, [location.pathname]);

  const handleNavigation = (itemId: string) => {
    setActiveItem(itemId);
    
    // Map itemId to routes
    let path = '/dashboard';
    
    // Super Admin routes
    if (userRole === 'SuperAdmin') {
      if (itemId === 'dashboard') {
        path = '/superadmin/dashboard';
      } else if (itemId === 'districts') {
        path = '/superadmin/districts';
      } else if (itemId === 'schools') {
        path = '/schools';
      } else if (itemId === 'games') {
        path = '/games';
      } else if (itemId === 'questions') {
        path = '/questions';
      } else if (itemId === 'analytics') {
        path = '/analytics';
      } else if (itemId === 'users') {
        path = '/users';
      } else if (itemId === 'superadmin-learning-paths') {
        path = '/superadmin/learning-paths';
      } else if (itemId === 'superadmin-learning-paths-create') {
        path = '/superadmin/learning-paths/create';
      } else if (itemId === 'classes') {
        path = '/classes';
      } else if (itemId === 'standards') {
        path = '/standards';
      } else if (itemId === 'system-config') {
        path = '/system-config';
      }
    } 
    // School Admin routes
    else if (userRole === 'SchoolAdmin') {
      if (itemId === 'schooladmin/dashboard') {
        path = '/schooladmin/dashboard';
      } else if (itemId === 'schooladmin/classes') {
        path = '/schooladmin/classes';
      } else if (itemId === 'schooladmin/games') {
        path = '/schooladmin/games';
      } else if (itemId === 'schooladmin/questions') {
        path = '/schooladmin/questions';
      } else if (itemId === 'schooladmin/standards') {
        path = '/schooladmin/standards';
      } else if (itemId === 'schooladmin/learning-paths') {
        path = '/schooladmin/learning-paths';
      } else if (itemId === 'schooladmin/learning-paths/create') {
        path = '/schooladmin/learning-paths/create';
      } else if (itemId === 'schooladmin/analytics') {
        path = '/schooladmin/analytics';
      } else if (itemId === 'schooladmin/users') {
        path = '/schooladmin/users';
      } else if (itemId === 'schooladmin/system-config') {
        path = '/schooladmin/system-config';
      }
    }
    // Admin routes
    else if (userRole === 'Admin') {
      if (itemId === 'dashboard') {
        path = '/dashboard';
      } else if (itemId === 'schools') {
        path = '/admin/schools';
      } else if (itemId === 'teachers') {
        path = '/admin/teachers';
      } else if (itemId === 'students') {
        path = '/admin/students';
      } else if (itemId === 'standards') {
        path = '/admin/standards';
      } else if (itemId === 'reports-district-progress') {
        path = '/admin/reports/district-progress';
      } else if (itemId === 'reports-school-performance') {
        path = '/admin/reports/school-performance';
      } else if (itemId === 'reports-standards-mastery') {
        path = '/admin/reports/standards-mastery';
      }
    } 
    // Teacher routes
    else {
      if (itemId === 'dashboard') {
        path = '/dashboard';
      } else if (itemId === 'classes') {
        path = '/classes';
      } else if (itemId === 'learning-paths-create') {
        path = '/learning-paths/create';
      } else if (itemId === 'learning-paths-manage') {
        path = '/learning-paths/manage';
      } else if (itemId === 'learning-paths-assign') {
        path = '/learning-paths/assign';
      } else if (itemId === 'students') {
        path = '/students';
      } else if (itemId === 'student-groups') {
        path = '/student-groups';
      } else if (itemId === 'reports') {
        path = '/reports';
      } else if (itemId === 'standards') {
        path = '/standards';
      } else if (itemId === 'games') {
        path = '/games';
      } else if (itemId === 'resources') {
        path = '/resources';
      } else if (itemId.startsWith('classes-')) {
        // Extract class ID from itemId (format: 'classes-{classId}')
        const classId = itemId.replace('classes-', '');
        path = `/classes/${classId}`;
      }
    }
    
    // Navigate to the appropriate route
    navigate(path);
  };

  // Get the logout function from AuthContext
  const { logout: authLogout } = useContext(AuthContext);
  
  const handleLogout = async () => {
    // Use the auth service to handle logout
    await authService.logout();
    // Use the AuthContext logout function to reset active role
    authLogout();
    // Navigate to the root page after logout
    navigate('/');
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Handle responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(prevState => !prevState);
    } else {
      setSidebarCollapsed(prevState => !prevState);
    }
  };

  // Set fixed sidebar width values for consistency
  const SIDEBAR_EXPANDED_WIDTH = '16rem'; // 256px or 64 in tailwind units
  const SIDEBAR_COLLAPSED_WIDTH = '5rem'; // 80px or 20 in tailwind units

  return (
    <div className="flex min-h-screen bg-gray-50 relative">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20" 
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar for mobile and desktop - fixed position */}
      <div 
        className={`
          fixed top-0 bottom-0 left-0 z-30
          ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}
          transition-transform duration-300 ease-in-out h-screen
        `}
        style={{ width: !isMobile ? (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH) : SIDEBAR_EXPANDED_WIDTH }}
      >
        <Sidebar 
          activeItem={activeItem}
          onItemClick={(item) => {
            handleNavigation(item);
            if (isMobile) setSidebarOpen(false);
          }}
          userRole={userRole}
          userName={userName}
          userAvatar={userAvatar}
          onLogout={handleLogout}
          isCollapsed={!isMobile && sidebarCollapsed}
          onToggleSidebar={handleToggleSidebar}
        />
      </div>

      {/* Main content with padding to account for fixed sidebar - using style for exact width */}
      <div 
        className="flex-1 flex flex-col transition-all duration-300"
        style={{ 
          marginLeft: !isMobile ? (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH) : '0px',
        }}
      >
        {/* Mobile header with menu button */}
        {isMobile && (
          <div className="sticky top-0 z-10 bg-white shadow-sm px-4 py-2 flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex-1 text-center">
              <img
                className="h-10 w-auto mx-auto"
                src="/assets/images/swunmath_logo.png"
                alt="SwunMath Logo"
              />
            </div>
            <div className="w-10"></div> {/* Spacer for balance */}
          </div>
        )}

        {/* Main content area with consistent padding */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <div className="w-full px-4 py-4">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;